import React, { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useGetNotificationSettingsQuery,
  useSaveNotificationSettingsMutation,
} from "../../slices/NotificationCenterSlice";
import {
  modalCancel,
  modalInput,
  modalPrimary,
  PortalModal,
} from "../clientPortal/PortalModal";
import { KindIcon } from "./NotificationParts";
import { NotificationPreferenceRow } from "./notifications.types";
import {
  errorText,
  isValidEmail,
  NOTIFICATION_CATEGORIES,
  settingsRows,
} from "./notifications.utils";

const Check: FC<{
  checked: boolean;
  disabled?: boolean;
  label: string;
  title?: string;
  onChange: (value: boolean) => void;
}> = ({ checked, disabled, label, title, onChange }) => (
  <input
    type={"checkbox"}
    aria-label={label}
    title={title}
    checked={checked}
    disabled={disabled}
    onChange={(e) => onChange(e.target.checked)}
    className={
      "h-[18px] w-[18px] cursor-pointer accent-[#6B4EFF] disabled:cursor-not-allowed disabled:opacity-50"
    }
  />
);

/** Per person: which notifications show in ProCharted and which are emailed. */
export const NotificationSettingsModal: FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data, isLoading, isError } = useGetNotificationSettingsQuery({
    orgId,
  });
  const [save, { isLoading: isSaving }] = useSaveNotificationSettingsMutation();

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [rows, setRows] = useState<NotificationPreferenceRow[]>(
    settingsRows(undefined),
  );
  const [problem, setProblem] = useState("");

  useEffect(() => {
    if (!data) return;
    setEmailEnabled(data.emailEnabled);
    setEmailAddress(data.emailAddress || "");
    setRows(settingsRows(data.events));
  }, [data]);

  const perEvent = data?.perEventAvailable !== false;

  const update = (event: string, change: Partial<NotificationPreferenceRow>) =>
    setRows((current) =>
      current.map((row) => (row.event === event ? { ...row, ...change } : row)),
    );

  const onSave = async () => {
    setProblem("");
    if (emailEnabled && !isValidEmail(emailAddress)) {
      setProblem("Enter a valid email address for your notification emails.");
      return;
    }
    try {
      await save({
        orgId,
        emailEnabled,
        emailAddress: emailAddress.trim(),
        events: perEvent ? rows : [],
        perEventAvailable: perEvent,
      }).unwrap();
      toast.success("Notification settings saved");
      onClose();
    } catch (error) {
      setProblem(errorText(error, "Your settings couldn't be saved."));
    }
  };

  return (
    <PortalModal
      title={"Notification settings"}
      subtitle={"Choose what you hear about, and how. Only you are affected."}
      onClose={onClose}
      width={620}
      footer={
        <>
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type={"button"}
            className={modalPrimary}
            disabled={isSaving || isLoading || isError}
            onClick={onSave}>
            {isSaving ? "Saving…" : "Save"}
          </button>
        </>
      }>
      {isLoading ? (
        <div className={"py-8 text-center text-[13.5px] text-[#9097A8]"}>
          Loading…
        </div>
      ) : isError ? (
        <div className={"py-8 text-center text-[13.5px] text-[#9097A8]"}>
          Your settings couldn&apos;t be loaded. Please try again.
        </div>
      ) : (
        <>
          <section
            className={
              "flex flex-col gap-3 rounded-[12px] border border-[#EDEFF3] p-4"
            }>
            <label
              className={
                "flex cursor-pointer items-center justify-between gap-4"
              }>
              <span className={"flex flex-col"}>
                <span className={"text-[14px] font-bold"}>
                  Email me about notifications
                </span>
                <span className={"text-[12.5px] text-[#6B7185]"}>
                  Turn this off to stop all notification emails.
                </span>
              </span>
              <input
                id={"notification-email-enabled"}
                type={"checkbox"}
                className={"toggle toggle-sm"}
                style={
                  emailEnabled
                    ? { background: "#6B4EFF", borderColor: "#6B4EFF" }
                    : undefined
                }
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
              />
            </label>
            {emailEnabled && (
              <div className={"flex flex-col gap-1.5"}>
                <label
                  htmlFor={"notification-email-address"}
                  className={"text-[12.5px] font-bold text-[#4A5163]"}>
                  Send them to
                </label>
                <input
                  id={"notification-email-address"}
                  type={"email"}
                  className={modalInput}
                  placeholder={"you@yourfirm.com"}
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                />
              </div>
            )}
          </section>

          {!perEvent && (
            <div
              className={
                "rounded-[10px] bg-[#FDF3E1] px-3 py-2 text-[12.5px] font-medium text-[#8A5A12]"
              }>
              Choosing per kind of notification needs a database update that
              hasn&apos;t been applied yet. Your email setting above can still
              be saved.
            </div>
          )}

          <div
            className={
              "overflow-hidden rounded-[12px] border border-[#EDEFF3]"
            }>
            <div
              className={
                "grid grid-cols-[1fr_112px_64px] items-center bg-[#F7F8FA] px-4 py-2 text-[11.5px] font-extrabold uppercase tracking-[0.4px] text-[#8A90A2]"
              }>
              <span>Notify me when…</span>
              <span className={"text-center"}>In ProCharted</span>
              <span className={"text-center"}>Email</span>
            </div>
            {rows.map((row) => {
              const meta = NOTIFICATION_CATEGORIES.find(
                (c) => c.key === row.event,
              );
              return (
                <div
                  key={row.event}
                  className={
                    "grid grid-cols-[1fr_112px_64px] items-center border-t border-[#F1F2F5] px-4 py-2.5"
                  }>
                  <span className={"flex min-w-0 items-center gap-3"}>
                    <KindIcon category={row.event} size={"sm"} />
                    <span className={"flex min-w-0 flex-col"}>
                      <span className={"text-[13.5px] font-bold"}>
                        {meta?.label}
                      </span>
                      <span className={"text-[12px] text-[#6B7185]"}>
                        {meta?.hint}
                      </span>
                    </span>
                  </span>
                  <span className={"flex justify-center"}>
                    <Check
                      label={`${meta?.label} in ProCharted`}
                      checked={row.inApp}
                      disabled={row.inAppLocked || !perEvent}
                      title={
                        row.inAppLocked
                          ? "Always shown in ProCharted"
                          : undefined
                      }
                      onChange={(value) => update(row.event, { inApp: value })}
                    />
                  </span>
                  <span className={"flex justify-center"}>
                    <Check
                      label={`${meta?.label} by email`}
                      checked={emailEnabled && row.email}
                      disabled={!emailEnabled || !perEvent}
                      title={
                        !emailEnabled
                          ? "Turn on notification emails first"
                          : undefined
                      }
                      onChange={(value) => update(row.event, { email: value })}
                    />
                  </span>
                </div>
              );
            })}
          </div>
          <p className={"m-0 text-[12px] text-[#8A90A2]"}>
            Mentions and automations always show in ProCharted. Client uploads
            sent within 30 minutes are grouped into one notification and one
            email.
          </p>
          {problem && (
            <div
              role={"alert"}
              className={
                "rounded-[10px] bg-[#FDECEC] px-3 py-2 text-[13px] font-semibold text-[#B42318]"
              }>
              {problem}
            </div>
          )}
        </>
      )}
    </PortalModal>
  );
};
